import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BrBox } from "@/components/BrBox";
import { BrBtn } from "@/components/BrBtn";
import { BrLabel } from "@/components/BrLabel";
import { Logo } from "@/components/Logo";
import { ChessBoard } from "@/chess/board/ChessBoard";
import {
  coordsFromSquare,
  fenToBoard,
  sideToMove,
  uciSquares,
  uciToSan,
  useChoiceDrill,
  useCompleteSession,
  useMarkItemSolved,
  isCorrectChoice,
  offsetShadow,
  TYPE,
  type TrainingSessionOut,
} from "@chess/shared";
import { useTheme } from "@/theme/ThemeProvider";
import { useViewport } from "@/util/viewport";

// Per-option color, by position in the choices list. Pulled from the palette's
// neutral choice hues so the color never hints at correctness.
function choiceColors(p: ReturnType<typeof useTheme>): string[] {
  return [p.choiceA, p.choiceB, p.choiceC];
}

interface Props {
  session: TrainingSessionOut;
}

/**
 * Fast multiple-choice drill: previews three candidate moves on the board
 * (color-coded arrows revealed one at a time), then shows three color-matched
 * buttons. The user has a few seconds to pick; a brief feedback flash plays and
 * the next puzzle auto-loads.
 */
export function ChoiceDrillPlay({ session }: Props) {
  const p = useTheme();
  const { isMobile, width } = useViewport();
  const nav = useNavigate();
  const [search] = useSearchParams();
  const fromGameId = search.get("from");
  const doneDestination = fromGameId ? `/app/analysis/${fromGameId}` : "/app";

  const markSolved = useMarkItemSolved();
  const completeSession = useCompleteSession();

  const drill = useChoiceDrill({
    items: session.items,
    onSolveItem: (item, outcome) => markSolved.mutate({ itemId: item.id, outcome }),
    onComplete: () => {
      if (!fromGameId) completeSession.mutate(session.id);
    },
  });

  const item = drill.current;
  const colors = choiceColors(p);
  const startFen = item?.position_fen ?? null;
  const playerColor: "w" | "b" = startFen ? sideToMove(startFen) : "w";
  const board = useMemo(
    () => (startFen ? fenToBoard(startFen) : Array.from({ length: 8 }, () => Array(8).fill(null))),
    [startFen],
  );

  // The choices, paired with their display color + SAN label.
  const options = useMemo(() => {
    if (!item) return [];
    return item.choices.map((c, i) => ({
      choice: c,
      color: colors[i % colors.length],
      san: uciToSan(startFen, c.uci) || c.uci,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, startFen]);

  // Arrows for the revealed candidates (preview reveals them one at a time; in
  // the choosing/feedback phases all are shown).
  const arrows = useMemo(() => {
    const out: { from: { row: number; col: number }; to: { row: number; col: number }; color: string }[] = [];
    options.slice(0, drill.revealedCount).forEach((o) => {
      const sq = uciSquares(o.choice.uci);
      if (sq) out.push({ from: coordsFromSquare(sq.from), to: coordsFromSquare(sq.to), color: o.color });
    });
    return out;
  }, [options, drill.revealedCount]);

  const finished = drill.completed;
  const correctUci = item?.choices.find((c) => c.kind === "best")?.uci ?? null;

  // --- completion screen ---------------------------------------------------
  if (finished) {
    return (
      <div style={{ minHeight: "100vh", background: p.bg, color: p.fg, display: "grid", placeItems: "center", padding: 24, fontFamily: TYPE.body }}>
        <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 18, alignItems: "center" }}>
          <BrLabel>FAST CHOICE · DONE</BrLabel>
          <div style={{ fontFamily: TYPE.display, fontSize: 48, fontWeight: 800, letterSpacing: -2, textTransform: "uppercase" }}>
            NICE WORK.
          </div>
          <BrBtn variant="accent" size="lg" onClick={() => nav(doneDestination)}>
            {fromGameId ? "Back to game →" : "Back to training →"}
          </BrBtn>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div style={{ minHeight: "100vh", background: p.bg, color: p.fg, display: "grid", placeItems: "center", padding: 24, fontFamily: TYPE.body, fontWeight: 700, textAlign: "center" }}>
        <div>
          <div style={{ marginBottom: 12 }}>No positions in this session.</div>
          <BrBtn variant="ghost" onClick={() => nav("/app/train")}>← Back</BrBtn>
        </div>
      </div>
    );
  }

  const BOARD = Math.min(isMobile ? width - 32 : 480, 480);
  const timeLimit = item.time_limit_seconds ?? null;
  const timeLeft = drill.remainingSec ?? 0;
  const timerDanger = timeLimit != null && timeLeft <= Math.max(2, Math.round(timeLimit * 0.34));

  return (
    <div style={{ minHeight: "100vh", background: p.bg, color: p.fg, fontFamily: TYPE.body }}>
      {/* Top bar */}
      <div style={{ borderBottom: `2px solid ${p.border}`, padding: isMobile ? "10px 14px" : "12px 24px", display: "flex", alignItems: "center", gap: 14 }}>
        <BrBtn size="xs" variant="surface" withShadow={false} onClick={() => nav(doneDestination)} ariaLabel="Back">←</BrBtn>
        {!isMobile && <Logo size={22} />}
        <div>
          <div style={{ fontSize: 11, fontFamily: TYPE.mono, letterSpacing: 1, textTransform: "uppercase" }}>FAST CHOICE</div>
          <div style={{ fontSize: 10, fontFamily: TYPE.mono, color: p.fgMuted, textTransform: "uppercase" }}>
            POSITION {drill.index + 1} / {drill.total}
          </div>
        </div>
        <div style={{ flex: 1 }} />
        {/* Countdown — only meaningful in the choosing phase. */}
        {timeLimit != null && (
          <div
            style={{
              fontFamily: TYPE.display,
              fontSize: 32,
              fontWeight: 800,
              letterSpacing: -1,
              minWidth: 56,
              textAlign: "right",
              color: drill.phase === "choosing" ? (timerDanger ? p.danger : p.fg) : p.fgFaint,
            }}
          >
            {drill.phase === "choosing" ? `0:${String(Math.max(0, timeLeft)).padStart(2, "0")}` : "—"}
          </div>
        )}
      </div>

      <main style={{ maxWidth: 720, margin: "0 auto", padding: isMobile ? "16px 14px 40px" : "28px 24px 56px", display: "flex", flexDirection: "column", gap: 18 }}>
        {/* Prompt banner */}
        <div style={{ background: p.fg, color: p.bg, padding: "12px 16px", border: `2px solid ${p.border}` }}>
          <div style={{ fontSize: 10, fontFamily: TYPE.mono, letterSpacing: 1.2, textTransform: "uppercase", opacity: 0.7 }}>
            {playerColor === "w" ? "WHITE TO MOVE" : "BLACK TO MOVE"}
          </div>
          <div style={{ fontFamily: TYPE.display, fontSize: isMobile ? 20 : 24, fontWeight: 800, letterSpacing: -0.6, textTransform: "uppercase", marginTop: 2 }}>
            {drill.phase === "preview" ? "Watch the options…" : drill.phase === "choosing" ? "Pick the best move." : drill.timedOut ? "Time's up." : isCorrectChoice(drill.picked) ? "Correct!" : "Not quite."}
          </div>
        </div>

        {/* Board with color-coded candidate arrows */}
        <div style={{ display: "flex", justifyContent: "center" }}>
          <ChessBoard
            palette={p}
            size={BOARD}
            position={board}
            arrows={arrows}
            flipped={playerColor === "b"}
            showCoords
            animMs={0}
          />
        </div>

        {/* Three color-coded option buttons */}
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 10 }}>
          {options.map((o, i) => {
            const isPicked = drill.picked?.uci === o.choice.uci;
            const isCorrect = o.choice.kind === "best";
            const revealed = drill.resolved;
            // After resolve, paint the correct one green and a wrong pick red;
            // otherwise each button wears its candidate color.
            let bg = o.color;
            let fg = "#ffffff";
            if (revealed) {
              if (isCorrect) { bg = p.success; fg = p.successFg; }
              else if (isPicked) { bg = p.danger; fg = p.dangerFg; }
              else { bg = p.card; fg = p.fgMuted; }
            }
            return (
              <ChoiceButton
                key={o.choice.uci + i}
                color={o.color}
                bg={bg}
                fg={fg}
                san={o.san}
                disabled={drill.phase !== "choosing"}
                dimmed={drill.phase === "preview" && drill.revealedCount <= i}
                showResultMark={revealed ? (isCorrect ? "ok" : isPicked ? "x" : null) : null}
                onClick={() => drill.pick(o.choice)}
                p={p}
              />
            );
          })}
        </div>

        {/* Reveal the correct move's SAN after resolve, for learning. */}
        {drill.resolved && correctUci && (
          <BrBox padding={12} shadowSize={3} label="BEST MOVE">
            <div style={{ fontFamily: TYPE.mono, fontSize: 16, fontWeight: 700, color: p.fg }}>
              {uciToSan(startFen, correctUci) || correctUci}
            </div>
          </BrBox>
        )}
      </main>
    </div>
  );
}

function ChoiceButton({
  color,
  bg,
  fg,
  san,
  disabled,
  dimmed,
  showResultMark,
  onClick,
  p,
}: {
  color: string;
  bg: string;
  fg: string;
  san: string;
  disabled: boolean;
  dimmed: boolean;
  showResultMark: "ok" | "x" | null;
  onClick: () => void;
  p: ReturnType<typeof useTheme>;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "16px 18px",
        background: bg,
        color: fg,
        border: `2px solid ${p.border}`,
        boxShadow: offsetShadow(p, 4, 4),
        cursor: disabled ? "default" : "pointer",
        fontFamily: TYPE.body,
        fontWeight: 800,
        fontSize: 18,
        textAlign: "left",
        opacity: dimmed ? 0.4 : 1,
        transition: "opacity 120ms ease",
        minHeight: 58,
      }}
    >
      {/* Color swatch tying the button to its board arrow. */}
      <span style={{ width: 16, height: 16, background: color, border: `2px solid ${p.border}`, flexShrink: 0 }} />
      <span style={{ flex: 1, textTransform: "uppercase", letterSpacing: -0.3 }}>Play {san}</span>
      {showResultMark === "ok" && <span style={{ fontSize: 20 }}>✓</span>}
      {showResultMark === "x" && <span style={{ fontSize: 20 }}>✕</span>}
    </button>
  );
}
